import { useState } from 'react';
import './App.css';
import { Link } from "react-router-dom";
import NetworkGraph from "./components/NetworkGraph";
import { IconGear } from "./components/Icons";

/* =========================================================================
   HelpDeskPlans
   Seção de cards de planos de Help Desk (ST 1 a ST 4), com:
   - alternância global "Por Usuário / Por Computador"
   - lista comparativa de recursos por card (check = incluso, traço = não incluso)

   Os cards não exibem mais valores — apenas nome, tag e recursos inclusos.
   Os itens de Suporte N1/N2/N3 são clicáveis e levam para a página de
   Suporte Técnico (ver SUPORTE_HREF abaixo).

   Uso: <HelpDeskPlans /> — o componente é independente, sem dependências de
   rotas ou de classes de outras páginas. Ajuste o "href" dos botões de CTA
   (ver CTA_HREF abaixo) para o link correto do seu formulário/contato.
   ========================================================================= */

const CTA_HREF = '/contato';
const SUPORTE_HREF = '/servicos/suporte/#suporte';

const TIERS = [
  {
    id: 'st1',
    label: 'ST 1',
    name: 'Suporte Essencial',
    tag: 'Ideal para quem está começando',
  },
  {
    id: 'st2',
    label: 'ST 2',
    name: 'Suporte Avançado',
    tag: 'Mais recursos de gestão de TI',
  },
  {
    id: 'st3',
    label: 'ST 3',
    name: 'Suporte Completo',
    tag: 'O mais escolhido pelos clientes',
    featured: true,
  },
  {
    id: 'st4',
    label: 'ST 4',
    name: 'Suporte Premium',
    tag: 'Gestão completa do parque tecnológico',
  },
];

/* Lista mestre de recursos, na mesma ordem exibida em todos os cards. */
const ALL_FEATURES = [
  'Acesso Remoto',
  'Suporte OnSite',
  'Suporte via WhatsApp',
  'Suporte Telefônico',
  'Suporte N1 (consultar)',
  'Suporte N2 (consultar)',
  'Suporte N3 (consultar)',
  'Suporte para Smartphone ou Tablet',
  'Inventário de Equipamento',
  'Inventário do Parque Tecnológico',
  'Controle de Usuário (UAC)',
  'RMM (Remote Monitoring and Management)',
  'Endpoint AntiVírus (Bitdefender GravityZone)',
  'Endpoint Firewall (Bitdefender GravityZone)',
  'Visita Técnica Inclusa',
];

/* Recursos cujo texto deve virar um link para a página de Suporte Técnico
   (seção com detalhes dos níveis N1/N2/N3). */
const LINKED_FEATURES = new Set([
  'Suporte N1 (consultar)',
  'Suporte N2 (consultar)',
  'Suporte N3 (consultar)',
]);

/* 1 = incluso, 0 = não incluso. Mesma ordem do ALL_FEATURES acima. */
const FEATURE_MATRIX = {
  usuario: {
    st1: [1, 1, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    st2: [1, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    st3: [1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0, 0, 0],
    /* Mesmo conjunto de canais de suporte do ST3 (recursos por dispositivo,
       como inventário/RMM/antivírus, não se aplicam ao plano por Usuário). */
    st4: [1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0, 0, 0],
  },
  computador: {
    st1: [1, 1, 1, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    st2: [1, 1, 1, 1, 1, 1, 0, 0, 1, 1, 1, 1, 1, 1, 0],
    st3: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0],
    st4: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
  },
};

function CheckIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M4 10.5L8 14.5L16 5.5"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function HelpDeskPlans() {
  const [planType, setPlanType] = useState('computador');

  return (
    <>
      <section className="page-hero">
        <div className="page-hero__bg"></div>
        <div className="page-hero__grid"></div>
        <NetworkGraph variant="hero" className="page-hero__network" />
        <div className="container">
          <div className="tag animate-up animate-up--d1">
            <IconGear style={{ width: "13px", height: "13px" }} />
            HELP DESK · SUPORTE TÉCNICO
          </div>
          <h1
            className="animate-up animate-up--d2"
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "clamp(36px,5vw,64px)",
              fontWeight: 700,
              letterSpacing: "-.03em",
              lineHeight: "1.1",
              marginBottom: "24px",
              color: "var(--text-primary)",
            }}
          >
            Help Desk
            <br />
            <span className="text-blue">
              Suporte técnico sempre à mão
            </span>
          </h1>
          <p
            className="animate-up animate-up--d3"
            style={{
              fontSize: "18px",
              color: "var(--text-secondary)",
              maxWidth: "560px",
              lineHeight: "1.7",
              marginBottom: "40px",
            }}
          >
            Conte com a sepolTECH para resolver os problemas de TI da sua empresa. Chamados ilimitados, atendimento remoto e presencial, com resposta rápida — tudo incluso na mensalidade fixa.
          </p>
          <div className="hero-actions-col animate-up animate-up--d4">
            <Link to="/contato" className="btn btn-primary">
              Solicitar proposta →
            </Link>
            {/*
            <Link to="/servicos/servicos" className="btn btn-outline">
              Ver todos os serviços
            </Link>*/}
          </div>
        </div>
      </section>
      <section className="hd-section" id="planos-helpdesk">
        <div className="hd-header">
          <h2>Planos de Help Desk</h2>
          <p>Suporte técnico sob demanda, com atendimento por usuário ou por computador — escolha o formato que melhor se encaixa na sua operação.</p>
        </div>

        <div className="hd-type-toggle" role="tablist" aria-label="Tipo de plano">
          <button
            type="button"
            role="tab"
            aria-selected={planType === 'usuario'}
            className={`hd-type-btn ${planType === 'usuario' ? 'hd-type-active' : ''}`}
            onClick={() => setPlanType('usuario')}
          >
            Por Usuário
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={planType === 'computador'}
            className={`hd-type-btn ${planType === 'computador' ? 'hd-type-active' : ''}`}
            onClick={() => setPlanType('computador')}
          >
            Por Computador
          </button>
        </div>

        <div className="hd-grid">
          {TIERS.map((tier) => {
            const features = FEATURE_MATRIX[planType][tier.id];

            return (
              <div
                key={tier.id}
                className={`hd-card ${tier.featured ? 'hd-card-featured' : ''}`}
              >
                {tier.featured && <span className="hd-featured-tag">Mais Popular</span>}

                <span className="hd-tier-badge">{tier.label}</span>
                <h3 className="hd-name">{tier.name}</h3>
                <p className="hd-tag">{tier.tag}</p>

                <div className="hd-divider" />

                <ul className="hd-features">
                  {ALL_FEATURES.map((label, i) => {
                    const included = features ? features[i] === 1 : false;
                    if (!included) return null;
                    const isLinked = LINKED_FEATURES.has(label);
                    return (
                      <li key={label} className="hd-feature-on">
                        <span className="hd-check">
                          <CheckIcon />
                        </span>
                        {isLinked ? (
                          <Link
                            to={SUPORTE_HREF}
                            style={{
                              color: 'var(--hd-blue-mid)',
                              textDecoration: 'underline',
                              textUnderlineOffset: '2px',
                            }}
                            className='suporte-link'
                          >
                            {label}
                          </Link>
                        ) : (
                          label
                        )}
                      </li>
                    );
                  })}
                </ul>

                <a
                  href={CTA_HREF}
                  className={`hd-cta ${tier.featured ? 'hd-cta-filled' : 'hd-cta-outline'}`}
                >
                  Assinar
                </a>
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
}
